import React from 'react';
import { useMediaQuery } from 'react-responsive';
import { faEnvelopeCircleCheck, faFileLines, faLocationDot, faMegaphone } from '@fortawesome/pro-regular-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import Button from 'components/UI/V3/Button/Button';
import { EmailMarketingProvider, EmailMarketingTab, useEmailMarketing } from './EmailMarketingProvider';
import { UnderlineTabs } from './UI/UnderlineTabs/UnderlineTabs';
import { PageHeader } from './UI/PageHeader/PageHeader';
import { FilterPill } from './UI/FilterPill/FilterPill';
import { LocationsScopePicker } from './UI/LocationsScopePicker/LocationsScopePicker';
import CampaignsTab from './Campaigns/CampaignsTab';
import TemplatesTab from './Templates/TemplatesTab';
import SenderTab from './Sender/SenderTab';
import CampaignWizard from './Wizard/CampaignWizard';
import styles from './EmailMarketingComponent.module.scss';

const TABS = [
    { id: 'campaigns', label: 'Campaigns', icon: faMegaphone },
    { id: 'templates', label: 'Templates', icon: faFileLines },
    { id: 'sender', label: 'Senders', icon: faEnvelopeCircleCheck }
];

const EmailMarketingContent: React.FC = () => {
    const {
        tab,
        setTab,
        wizardDraft,
        openNewCampaign,
        locations,
        scopeIds,
        setScopeIds,
        isAllScope,
        describeLocations
    } = useEmailMarketing();
    const isMobile = useMediaQuery({ maxWidth: 600 });

    return (
        <div className={styles.root}>
            <PageHeader
                title="Email Marketing"
                description="Create and send email campaigns to customers across all your locations."
                below={
                    <FilterPill
                        size="large"
                        icon={faLocationDot}
                        label={isAllScope ? `All locations (${locations.length})` : describeLocations(scopeIds)}
                    >
                        {close => (
                            <LocationsScopePicker
                                locations={locations}
                                selectedIds={scopeIds}
                                onApply={ids => {
                                    setScopeIds(ids);
                                    close();
                                }}
                            />
                        )}
                    </FilterPill>
                }
                actions={
                    <Button
                        variant="outlined"
                        leftIcon={faPlus}
                        label={isMobile ? 'New' : 'New campaign'}
                        onClick={() => openNewCampaign()}
                    />
                }
            />

            <UnderlineTabs
                tabs={TABS}
                selectedId={tab}
                onSelect={id => setTab(id as EmailMarketingTab)}
            />

            <div className={styles.content}>
                {tab === 'campaigns' && <CampaignsTab />}
                {tab === 'templates' && <TemplatesTab />}
                {tab === 'sender' && <SenderTab />}
            </div>

            {wizardDraft && <CampaignWizard initialDraft={wizardDraft} />}
        </div>
    );
};

export const EmailMarketingComponent: React.FC = () => (
    <EmailMarketingProvider>
        <EmailMarketingContent />
    </EmailMarketingProvider>
);

export default EmailMarketingComponent;
